import { create } from 'zustand';

export interface PromptOptions {
  title: string;
  message: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'primary';
  /** Minimum input length required (default: 1) */
  minLength?: number;
}

interface PromptState {
  isOpen: boolean;
  options: PromptOptions | null;
  value: string;
  resolve: ((value: string | null) => void) | null;

  prompt: (options: PromptOptions) => Promise<string | null>;
  setValue: (value: string) => void;
  handleConfirm: () => void;
  handleCancel: () => void;
}

export const usePromptStore = create<PromptState>((set, get) => ({
  isOpen: false,
  options: null,
  value: '',
  resolve: null,

  prompt: (options: PromptOptions) => {
    return new Promise<string | null>((resolve) => {
      set({
        isOpen: true,
        options,
        value: '',
        resolve,
      });
    });
  },

  setValue: (value: string) => {
    set({ value });
  },

  handleConfirm: () => {
    const { resolve, value, options } = get();
    const minLength = options?.minLength ?? 1;
    if (value.trim().length < minLength) return;
    if (resolve) {
      resolve(value.trim());
    }
    set({ isOpen: false, options: null, value: '', resolve: null });
  },

  handleCancel: () => {
    const { resolve } = get();
    if (resolve) {
      resolve(null);
    }
    set({ isOpen: false, options: null, value: '', resolve: null });
  },
}));

/** Helper function for easy usage: const reason = await prompt({ title, message }) */
export const prompt = (options: PromptOptions): Promise<string | null> => {
  return usePromptStore.getState().prompt(options);
};
